/**
 * @fileoverview Resend-verification-code mutation hook.
 * @description Wraps the resend-otp API call in a TanStack mutation for loading/error state, and
 * on success replaces the stored challenge's id and parses its refreshed absolute ISO expiry/
 * resend timestamps into epoch milliseconds, so the countdown (and the Resend button's own
 * cooldown) restarts.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'

import {
  resendTwoFactorCode,
  type ResendTwoFactorRequest,
  type ResendTwoFactorResponse,
} from '@/features/auth/services/two-factor.service'
import { useTwoFactorChallenge } from '@/shared/lib/auth/useTwoFactorChallenge'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Requests a fresh verification code and, on success, replaces the stored challenge's
 * token and extends its expiry/resend-cooldown.
 * @returns {ReturnType<typeof useMutation<ResendTwoFactorResponse, ApiError,
 * ResendTwoFactorRequest>>} The mutation object (mutate, isPending, error, ...).
 */
export function useResendTwoFactorCode() {
  const { challenge, setChallenge } = useTwoFactorChallenge()

  return useMutation<ResendTwoFactorResponse, ApiError, ResendTwoFactorRequest>({
    mutationFn: resendTwoFactorCode,
    onSuccess: (response) => {
      if (!challenge) {
        return
      }

      setChallenge({
        challengeId: response.challengeId,
        email: challenge.email,
        destinationMasked: response.destinationMasked,
        expiresAt: new Date(response.expiresAt).getTime(),
        resendAvailableAt: new Date(response.resendAvailableAt).getTime(),
      })
    },
  })
}
